import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';

import { useNavigate } from 'react-router-dom';
import { Document, LoadingProcessData, Page, pdfjs } from 'react-pdf';
import { PDFExport } from '@progress/kendo-react-pdf';

import { tutorialConstants } from '../../../utils/constants';
import { ProgressComment, TutorialInstruction } from '../../../utils/models';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { getTutorials, setActiveTutorial } from '../../../store/tutorials';
import { formatTimeWithRelativeStamp, getExtractedInspectionName, getPageRange, paginate } from '../../../utils/utilities';
import { getCanAccessReportProgress } from '../../../utils/session';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCoverPage from '../../custom-reports/custom-general/custom-cover-page';
import CustomInfoSheet from '../../custom-reports/custom-general/custom-info-sheet';
import CustomSection from '../../custom-reports/custom-general/custom-section';
import Header from '../../blocks/header';
import Elevation from '../../themed/instruction-modals/elevation';
import Instruction from '../../themed/instruction-modals/instruction';
import Loader from '../../themed/loader';
import Modal from '../../themed/modal';
import NoItems from '../../blocks/no-items';
import PDFTablePage from '../../themed/pdf/pdfTablePage';
import Pagination from '../../themed/pagination';
import Progress from '../../themed/progress';
import ProgressCommentTrail from '../../blocks/progress-comment-trail';
import ProgressCommentForm from '../../forms/progressCommentForm';
import Select from '../../themed/select/select';
import SyncTime from '../../blocks/sync-time';

import useProjectDetails from '../../../hooks/useProjectDetails';

import classNames from './style.module.scss';
import config from '../../../utils/config';
import routes from '../../../utils/routes';
import toastService from '../../../services/toastService';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const ProgressReport: React.FC = () => {
	const [isExportVisible, setExportVisible] = useState(false);
 	const [currentPage, setCurrentPage] = useState(1);
	const [currentCommentPage, setCurrentCommentPage] = useState(1);
	const [commentPageSize, setCommentPageSize] = useState(10);
	const [documentWidth, setDocumentWidth] = useState(100);
	const [loadProgress, setLoadProgress] = useState(0);
	const [numberOfPages, setNumberOfPages] = useState(0);
	const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
	const [activeTab, setActiveTab] = useState(0);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const { activeTutorial } = useAppSelector(getTutorials);
	
	const pdfExportComponent = useRef<PDFExport>(null);
	const documentRef = useRef<HTMLDivElement>(null);
	const details = useProjectDetails();

	const commentsToRender = useMemo(() => {
		if (details.projectDetails) {
			const comments = details.projectDetails?.report.comment ?? [];
			return activeTab === 0 ? comments : comments.filter((comment) => comment.pageNumber === currentPage);
		}

		return [];
	}, [activeTab, currentPage, details.projectDetails]);

	const paginatedComments = useMemo(() => {
		const sorted = _.orderBy(commentsToRender, ['commentDate'], 'asc');
		const paginated = paginate(sorted, currentCommentPage, commentPageSize);

		return { data: paginated, totalComments: commentsToRender.length };
	}, [commentsToRender, currentCommentPage, commentPageSize]);

	const handleLoadProgress = (data: LoadingProcessData): void => {
		const progress: number = (data.loaded * 100) / data.total;
		setLoadProgress(progress);
	};

	const handleLoadSuccess = (data: { numPages: number }) => {
		setNumberOfPages(data.numPages);
		setIsDocumentLoaded(true);
	};

	const handleActiveTutorial = (tutorialId: number) => {
		dispatch(setActiveTutorial(tutorialId));

		if (tutorialId === tutorialConstants.PROFILE_SETTINGS) {
			navigate(routes.profileSettings.fullPath);
		}
	};

	const handleCommentExport = (): void => {
		const pdf: PDFExport | null = pdfExportComponent.current;
		if (!pdf) return toastService.error('Document not available');

		pdf.save();
	};

	const handlePageClick = (page: number) => {
		setCurrentPage(page);
		documentRef.current?.scrollIntoView({ behavior: 'smooth' });
	}

	const renderProgressCommentForm = () => {
		if (!details.projectDetails) return null;

		return (
			<Elevation isElevated={activeTutorial === tutorialConstants.ADD_PROJECT_COMMENT}>
				<Conditional isVisible={details.projectDetails.isActive}>
					<ProgressCommentForm currentPage={currentPage} />
				</Conditional>
			</Elevation>
		);
	};

	const renderExportableComments = () => {
		if (!details.projectDetails || details.error) return null;

		const { inspectionTypeName } = details.projectDetails;

		return (
				<Modal isVisible={isExportVisible} onClose={() => setExportVisible(false)} >
						<PDFExport
							paperSize='A4'
							scale={0.5}
							author="Arridex"
							fileName={`Comments for Report ${details.projectDetails?.documentNumber}.pdf`}
							title={details.projectDetails?.projectTitle}
							producer="RSMG"
							forcePageBreak='.page-break'
							ref={pdfExportComponent}
						>
							<CustomCoverPage reportTitle={getExtractedInspectionName(inspectionTypeName)} />

							<div className="page-break">
								<CustomInfoSheet
									projectDetails={details.projectDetails}
									workOrderNumber=''
									reportTitle=''
								/>
							</div>

							<div className="page-break">
								<CustomSection sectionTitle='Report Comments'>
									<div className="p-2">
										<PDFTablePage 
											data={paginatedComments.data} 
											columns={[
												{
													path: 'commentDate',
													label: 'Comment Date',
													content: (comment: ProgressComment) => <span>{formatTimeWithRelativeStamp(comment.commentDate)}</span>
												},
												{
													path: 'commentByName',
													label: 'Commenter',
												},
												{
													path: 'body',
													label: 'Comment',
												},
												{
													path: 'pageNumber',
													label: 'Page Number',
												},
											]}
										/>
									</div>
								</CustomSection>
							</div>
						</PDFExport>

						<div className="p-2">
							<Button 
								label='Click to export to PDF' 
								onClick={handleCommentExport} 
							/>
						</div>
				</Modal>
			);
	};

	const handleSetDocumentWidth = () => {
		const documentDom = documentRef.current;
		if (documentDom) setDocumentWidth(documentDom.clientWidth);
	};

	useLayoutEffect(() => {
		handleSetDocumentWidth();
	}, [details.projectDetails]);

	useEffect(() => {
		window.addEventListener('resize', function (event) {
			handleSetDocumentWidth();
		});

		return window.removeEventListener('resize', function (event) {
			handleSetDocumentWidth();
		});
	}, []);
	
	useEffect(() => {
		if (!config.enableReportComment) {
			navigate(routes.projects.fullPath);
		}
	}, [navigate]);

	useEffect(() => {
		if (details.projectDetails && !getCanAccessReportProgress(details.projectDetails)) {
			navigate(routes.unauthorized.fullPath);
		}
	}, [details.projectDetails, navigate]);

	const isActive = activeTutorial === tutorialConstants.VIEW_UNPUBLISHED_REPORT;
	const isComments = activeTutorial === tutorialConstants.VIEW_PROJECT_COMMENTS;

	return (
		<React.Fragment>
			<Progress progress={loadProgress} />

			{instructions.map((instruction) => (
				<Instruction
					key={instruction.id}
					isVisible={activeTutorial === instruction.id}
					onProceed={() => handleActiveTutorial(instruction.next)}
					title={instruction.title}
					message={instruction.message}
				/>
			))}

			<Header
				canViewCTA
				onCTAClick={() => navigate(-1)}
				subtitle={details.projectDetails?.projectTitle ?? 'N/A'}
				buttonIcon="kanban-fill"
				buttonTitle="Back to Project Details"
			/>

			{renderExportableComments()}

			<div className={classNames.progress}>
				<Conditional isVisible={details.error}>
					<div className="bg-white border p-3 d-flex justify-content-center mb-4">
						<Button
							label="Retry"
							disabled={details.isFetching}
							onClick={details.fetchProjectDetails}
						/>
					</div>
				</Conditional>

				<Conditional isVisible={details.projectDetails !== null}>
					<SyncTime lastSynced={details.lastUpdated} />

					<div className="divider my-4"></div>

					<Elevation isElevated={isActive}>
						<div ref={documentRef} className="bg-white border">
							{details.projectDetails && (
								<Document
									file={details.projectDetails.report.savedProgressUrl}
									onLoadProgress={handleLoadProgress}
									onLoadSuccess={handleLoadSuccess}
								>
									<Page
										pageNumber={currentPage}
										width={documentWidth}
										scale={1}
									/>
								</Document>
							)}
						</div>
					</Elevation>

					{details.projectDetails && (
						<React.Fragment>
							<Elevation isElevated={isActive}>
								<Conditional isVisible={isDocumentLoaded}>									
									<div className="divider my-4"></div>

									{renderProgressCommentForm()}

									<div className="divider my-4"></div>

									<div className="bg-white border ps-4 d-flex justify-content-between align-items-center ">
										<div className={classNames.progressPaging}>
											Showing page <span>{currentPage} </span> of <span>{numberOfPages}</span>
										</div>

										<Pagination
											itemsCount={numberOfPages}
											pageSize={1}
											currentPage={currentPage}
											onPageChange={(page: number) => setCurrentPage(page)}
										/>
									</div>
								</Conditional>
							</Elevation>

							<div className="divider my-4"></div>
									
							<Elevation isElevated={isComments}>
								<h2 className={classNames.progressTrailTitle}>Comments</h2>

								<div className="divider my-4"></div>

								<div className="d-flex justify-content-between">
									<div className={classNames.progressTabs}>
										{PAGE_VIEW.map((view) => (
											<button 
												key={view.name}
												className={view.id === activeTab ? classNames.progressTabsActive : classNames.progressTabsInactive}
												onClick={() => setActiveTab(view.id)}
											>
												{view.name}
											</button>
										))}
									</div>

									<div className="d-flex bg-white border p-2 pb-0">
										<Select 
											name='commentPageSize' 
											options={getPageRange(20).map((page) => ({ id: page, name: page}))} 
											value={commentPageSize}
											onChange={(e) => setCommentPageSize(parseInt(e.target.value))}
										/>

										<Conditional isVisible={paginatedComments.totalComments > 0}>
											<div className="ms-3">
												<Button 
													label='Download Comments' 
													onClick={() => setExportVisible(true)} 
													style={{ marginTop: 0 }} 
												/>
											</div>
										</Conditional>
									</div>
								</div>

								<Conditional isVisible={paginatedComments.totalComments > 0}>
									<ProgressCommentTrail comments={paginatedComments.data} onPageClick={handlePageClick} />

									<Pagination
										itemsCount={paginatedComments.totalComments}
										pageSize={commentPageSize}
										currentPage={currentCommentPage}
										onPageChange={(page: number) => setCurrentCommentPage(page)}
									/>
								</Conditional>

								<div className="mt-4">
									<NoItems isVisible={paginatedComments.totalComments === 0} onRefresh={details.fetchProjectDetails} />
								</div>
							</Elevation>
						</React.Fragment>
					)}

					<div className="divider my-4"></div>
				</Conditional>

				<Loader isVisible={details.isFetching} />
			</div>
		</React.Fragment>
	);
};

const instructions: TutorialInstruction[] = [
	{
		id: tutorialConstants.VIEW_UNPUBLISHED_REPORT,
		next: tutorialConstants.ADD_PROJECT_COMMENT,
		title: 'Track Realtime Progress!',
		message:
			'You will be able to see the unpublished progress for the active report here. This page is only visible to the engineer and the approver',
	},
	{
		id: tutorialConstants.ADD_PROJECT_COMMENT,
		next: tutorialConstants.VIEW_PROJECT_COMMENTS,
		title: 'Give Feedback!',
		message:
			'You can give feedback on the report while generation is ongoing. You will be updated via email on every comment ',
	},
	{
		id: tutorialConstants.VIEW_PROJECT_COMMENTS,
		next: tutorialConstants.PROFILE_SETTINGS,
		title: 'View Comment Trail!',
		message: 'Comment history is also visible on this page!',
	},
];

const PAGE_VIEW = [
	{
		id: 0,
		name: 'All comments'
	},
	{
		id: 1,
		name: 'Comments Per Page'
	}
]

export default ProgressReport;
